/**
 * AI Summary Context
 * 
 * Provides global state for AI summary availability across the application.
 * Used to conditionally show/hide AI features based on whether the user's team
 * has any schools with AI summaries in the database.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../auth/AuthContext';
import { getSchoolsWithAISummaries } from '../services/aiService';

const AISummaryContext = createContext();

/**
 * Custom hook to access AI summary availability state
 * @returns {Object} { hasAISummaries: boolean, loading: boolean, schoolCount: number, isAdmin: boolean, refresh: function }
 */
export const useAISummaryAvailability = () => {
  const context = useContext(AISummaryContext);

  // The provider sits above the router, so it is mounted on City Explorer
  // pages too. Subscribing here means the (School Explorer only) fetch is
  // driven by actual consumers rather than by every authenticated page load.
  const subscribe = context?.subscribe;
  useEffect(() => subscribe?.(), [subscribe]);

  if (!context) {
    throw new Error('useAISummaryAvailability must be used within AISummaryProvider');
  }
  return context;
};

/**
 * AI Summary Provider Component
 * Wraps the app to provide global AI summary availability state
 */
export const AISummaryProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [hasAISummaries, setHasAISummaries] = useState(false);
  const [schoolCount, setSchoolCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [aiSchools, setAISchools] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [consumerCount, setConsumerCount] = useState(0);
  const userId = user?.uid || null;

  const subscribe = useCallback(() => {
    setConsumerCount((count) => count + 1);
    return () => setConsumerCount((count) => count - 1);
  }, []);

  const hasConsumers = consumerCount > 0;

  // Fetch availability when user changes or auth completes
  useEffect(() => {
    /**
     * Fetch AI summary availability from the backend
     */
    const fetchAvailability = async () => {
      if (!userId) {
        setHasAISummaries(false);
        setSchoolCount(0);
        setIsAdmin(false);
        setAISchools([]);
        setPortfolios([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Fetch schools list — this also tells us whether summaries are available,
        // so we avoid a separate has-ai-summaries round trip.
        const result = await getSchoolsWithAISummaries();
        const schools = result.schools || [];
        const publishedPortfolios = result.portfolios || [];
        const admin = result.is_admin || false;
        setIsAdmin(admin);
        setAISchools(schools);
        setPortfolios(publishedPortfolios);
        setHasAISummaries(admin || schools.length > 0 || publishedPortfolios.length > 0);
        setSchoolCount(admin ? -1 : schools.length);
      } catch (err) {
        console.error('Error checking AI summary availability:', err);
        setError(err.message);
        setHasAISummaries(false);
        setSchoolCount(0);
        setIsAdmin(false);
        setAISchools([]);
        setPortfolios([]);
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading && hasConsumers) {
      fetchAvailability();
    }
    // Keyed on the uid rather than the user object: AuthContext replaces
    // `user` on every token refresh, which used to refetch each time.
  }, [userId, authLoading, hasConsumers]);

  /**
   * Refresh the availability check (useful after AI summaries are generated)
   */
  const refresh = () => {
    const refetchAvailability = async () => {
      if (!user) return;
      setLoading(true);
      try {
        const result = await getSchoolsWithAISummaries();
        const schools = result.schools || [];
        const publishedPortfolios = result.portfolios || [];
        const admin = result.is_admin || false;
        setIsAdmin(admin);
        setAISchools(schools);
        setPortfolios(publishedPortfolios);
        setHasAISummaries(admin || schools.length > 0 || publishedPortfolios.length > 0);
        setSchoolCount(admin ? -1 : schools.length);
      } catch (err) {
        console.error('Error refreshing AI summary availability:', err);
      } finally {
        setLoading(false);
      }
    };
    refetchAvailability();
  };

  const value = {
    hasAISummaries,
    schoolCount,
    isAdmin,
    aiSchools,
    portfolios,
    loading,
    error,
    refresh,
    subscribe
  };

  return (
    <AISummaryContext.Provider value={value}>
      {children}
    </AISummaryContext.Provider>
  );
};

export default AISummaryContext;

